// Hook para gestionar reservas desde el panel administrativo
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'

import { ApiClient } from '../services/api'

// Interfaces
export interface EspacioEnReserva {
  id: string
  nombre: string
  codigo: string
  tarifa_unitaria: number
  fecha_entrada?: string  // ✅ Fechas individuales por espacio
  fecha_salida?: string
  unidad_reserva?: string  // 'dia' o 'noche'
}

export interface Reserva {
  id: string
  codigo?: string
  huesped_nombre?: string
  huesped_email?: string
  cliente_nombre?: string
  cliente_email?: string
  cliente_telefono?: string
  cliente_documento?: string
  cliente_tipo_documento?: string
  espacio_nombre?: string
  espacio_id?: string
  espacio_codigo?: string
  tipo_espacio?: string
  fecha_entrada: string
  fecha_salida: string
  cantidad_huespedes: number
  monto_total: number
  monto_pagado?: number  // Dinero real pagado (sumado desde tabla pagos)
  estado: 'confirmada' | 'pendiente' | 'cancelada' | 'en_proceso' | 'completada'
  estado_pago: 'pendiente' | 'pagado' | 'reembolsado'
  fecha_creacion: string
  solicitudes_especiales?: string
  unidad_reserva?: string  // 'dia' o 'noche'
  espacios?: EspacioEnReserva[]  // ✅ Array de espacios desde reserva_espacios
  tipo_reserva?: 'manual' | 'invitado'  // Tipo de reserva
}

export interface ReservasListResponse {
  total: number
  reservas: Reserva[]
}

export interface ReservasFiltros {
  estado?: string
  estado_pago?: string
  fecha_desde?: string
  fecha_hasta?: string
  buscar?: string
  limit?: number
  offset?: number
}

// Query keys
export const QUERY_KEYS = {
  RESERVAS_ADMIN: 'reservas-admin',
  RESERVA_DETAIL: 'reserva-detail',
} as const

// Hook para listar reservas con filtros
export const useReservationsAdmin = (filtros?: ReservasFiltros) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RESERVAS_ADMIN, filtros],
    queryFn: async () => {
      const params = new URLSearchParams()

      if (filtros?.estado) {
        params.append('estado', filtros.estado)
      }
      if (filtros?.estado_pago) {
        params.append('estado_pago', filtros.estado_pago)
      }
      if (filtros?.fecha_desde) {
        params.append('fecha_desde', filtros.fecha_desde)
      }
      if (filtros?.fecha_hasta) {
        params.append('fecha_hasta', filtros.fecha_hasta)
      }
      if (filtros?.buscar) {
        params.append('buscar', filtros.buscar)
      }
      if (filtros?.limit) {
        params.append('limit', filtros.limit.toString())
      }
      if (filtros?.offset) {
        params.append('offset', filtros.offset.toString())
      }

      const response = await ApiClient.get<ReservasListResponse>(
        `/api/admin-protected/reservas?${params.toString()}`
      )

      return response
    },
    staleTime: 2 * 60 * 1000, // 2 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })
}

// Hook para obtener detalle de una reserva
export const useReservaDetail = (reservaId?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.RESERVA_DETAIL, reservaId],
    queryFn: async () => {
      if (!reservaId) throw new Error('reservaId es requerido')

      const reserva = await ApiClient.get<Reserva>(
        `/api/admin-protected/reservas/${reservaId}`
      )

      return reserva
    },
    enabled: !!reservaId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  })
}

// Hook para actualizar estado de una reserva (cuando esté implementado)
export const useActualizarReservaEstado = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      reservaId,
      estado,
    }: {
      reservaId: string
      estado: string
    }) => {
      const response = await ApiClient.put<Reserva>(
        `/api/admin-protected/reservas/${reservaId}`,
        { estado }
      )
      return response.data
    },
    onSuccess: (data) => {
      toastSuccess('Reserva actualizada exitosamente')

      // Invalidar lista de reservas y detalle
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVAS_ADMIN],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVA_DETAIL, data.id],
      })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Error al actualizar reserva'
      toastError(message)
    },
  })
}

// Hook para cancelar una reserva (cuando esté implementado)
export const useCancelarReserva = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (reservaId: string) => {
      const response = await ApiClient.delete(
        `/api/admin-protected/reservas/${reservaId}`
      )
      return response
    },
    onSuccess: (data) => {
      toastSuccess('Reserva cancelada exitosamente')

      // Invalidar lista de reservas
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVAS_ADMIN],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.RESERVA_DETAIL],
      })
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail || 'Error al cancelar reserva'
      toastError(message)
    },
  })
}
